import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { io, type Socket } from "socket.io-client";
import {
  Headset,
  Loader2,
  MessageSquare,
  Plus,
  Send,
  ShieldCheck,
} from "lucide-react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHead } from "@/components/bits";
import { useAuth } from "@/features/auth/auth-context";
import { useI18n } from "@/i18n/i18n-context";
import { useToast } from "@/hooks/use-toast";
import { runtime } from "@/config/runtime";
import { publicApi } from "@/services/api-client";
import { tokenStore } from "@/services/token-store";
import { cn } from "@/lib/utils";

type TicketStatus =
  "OPEN" | "IN_PROGRESS" | "WAITING_MERCHANT" | "RESOLVED" | "CLOSED";
type TicketMessage = {
  id: string;
  author_type: string;
  visibility: "PUBLIC" | "INTERNAL";
  message: string;
  created_at: string;
};
type Ticket = {
  id: string;
  ticket_number: string;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: TicketStatus;
  assigned_role: string;
  last_message_at: string | null;
  messages?: TicketMessage[];
};
type TicketPage = {
  data: Ticket[];
  current_page: number;
  last_page: number;
  total: number;
};
type SupportSocket = Socket;

const supportRoles = [
  "SUPER_ADMIN",
  "ADMIN",
  "OPERATIONS_MANAGER",
  "OPERATIONS_AGENT",
  "FINANCE_MANAGER",
  "FINANCE_AGENT",
  "COMPLIANCE_MANAGER",
  "COMPLIANCE_AGENT",
  "RISK_MANAGER",
  "RISK_AGENT",
  "TECHNICAL_ADMIN",
  "AUDITOR",
];
const statusValues: TicketStatus[] = [
  "OPEN",
  "IN_PROGRESS",
  "WAITING_MERCHANT",
  "RESOLVED",
  "CLOSED",
];
const queueRoles = [
  "OPERATIONS_AGENT",
  "FINANCE_AGENT",
  "COMPLIANCE_AGENT",
  "RISK_AGENT",
  "TECHNICAL_ADMIN",
];

export default function SupportTicketsPage() {
  const { identity } = useAuth();
  const { t } = useI18n();
  const { toast } = useToast();
  const [location] = useLocation();
  const adminMode = location.startsWith("/admin/support");
  const canManage =
    identity?.roles.some((role) =>
      [
        "SUPER_ADMIN",
        "ADMIN",
        "OPERATIONS_MANAGER",
        "FINANCE_MANAGER",
        "COMPLIANCE_MANAGER",
        "RISK_MANAGER",
        "TECHNICAL_ADMIN",
      ].includes(role),
    ) ?? false;
  const isSupportRole =
    identity?.roles.some((role) => supportRoles.includes(role)) ?? false;
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [selected, setSelected] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [connected, setConnected] = useState(false);
  const [socket, setSocket] = useState<SupportSocket | null>(null);
  const [draft, setDraft] = useState("");
  const [subject, setSubject] = useState("");
  const [newMessage, setNewMessage] = useState("");
  const [category, setCategory] = useState("GENERAL");
  const [internalNote, setInternalNote] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const selectedRef = useRef(selected);
  const loadTicketsRef = useRef<() => Promise<void>>(async () => undefined);
  selectedRef.current = selected;

  const apiBase = adminMode ? "/admin/support-tickets" : "/support/tickets";
  const detailPath = (id: string) => `${apiBase}/${id}`;
  const visibleTickets = useMemo(() => tickets, [tickets]);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ per_page: "50" });
      if (statusFilter) params.set("status", statusFilter);
      if (adminMode && search.trim()) params.set("search", search.trim());
      const result = await publicApi<TicketPage>(
        `${apiBase}?${params.toString()}`,
      );
      setTickets(result.data.data ?? []);
    } catch (error) {
      toast({
        title: t("support.loadFailed"),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [adminMode, apiBase, search, statusFilter, t, toast]);
  loadTicketsRef.current = loadTickets;

  useEffect(() => {
    void loadTickets();
  }, [loadTickets]);

  useEffect(() => {
    if (!adminMode) return;
    const timer = window.setInterval(
      () => void loadTicketsRef.current(),
      15_000,
    );
    return () => window.clearInterval(timer);
  }, [adminMode]);

  useEffect(() => {
    const client = io(runtime.merchantAssistantSocketUrl, {
      path: "/socket.io",
      transports: ["websocket", "polling"],
      auth: (callback) =>
        callback({ token: tokenStore.get()?.accessToken ?? "" }),
      reconnection: true,
      reconnectionAttempts: 8,
      reconnectionDelay: 5_000,
      reconnectionDelayMax: 30_000,
      randomizationFactor: 0.5,
      timeout: 20_000,
    }) as SupportSocket;
    setSocket(client);
    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);
    const onMessage = (event: {
      ticket_id: string;
      message?: TicketMessage;
      ticket?: Ticket;
    }) => {
      const current = selectedRef.current;
      if (!current || event.ticket_id !== current.id) return;
      if (
        event.message?.visibility === "PUBLIC" ||
        (adminMode && event.message?.visibility === "INTERNAL")
      ) {
        setSelected((previous) =>
          previous
            ? {
                ...previous,
                ...(event.ticket ?? {}),
                messages: [
                  ...(previous.messages ?? []).filter(
                    (message) => message.id !== event.message?.id,
                  ),
                  ...(event.message ? [event.message] : []),
                ],
              }
            : current,
        );
      }
      void loadTicketsRef.current();
    };
    client.on("connect", onConnect);
    client.on("disconnect", onDisconnect);
    client.on("support:message", onMessage);
    return () => {
      client.off("connect", onConnect);
      client.off("disconnect", onDisconnect);
      client.off("support:message", onMessage);
      client.disconnect();
      setSocket(null);
      setConnected(false);
    };
  }, [adminMode]);

  const openTicket = async (ticket: Ticket) => {
    setBusy(true);
    try {
      const result = await publicApi<Ticket>(detailPath(ticket.id));
      setSelected(result.data);
      socket?.emit(
        "support:join",
        { ticket_id: ticket.id },
        (ack: { success: boolean; error?: { message?: string } }) => {
          if (!ack.success)
            toast({
              title: t("support.connectionFailed"),
              description: ack.error?.message,
              variant: "destructive",
            });
        },
      );
    } catch (error) {
      toast({
        title: t("support.loadFailed"),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const createTicket = async (event: FormEvent) => {
    event.preventDefault();
    if (!subject.trim() || !newMessage.trim()) return;
    setBusy(true);
    try {
      const result = await publicApi<Ticket>(apiBase, {
        method: "POST",
        body: { subject, message: newMessage, category },
      });
      setSubject("");
      setNewMessage("");
      setSelected(result.data);
      await loadTickets();
      socket?.emit(
        "support:join",
        { ticket_id: result.data.id },
        () => undefined,
      );
      toast({
        title: t("support.created"),
        description: result.data.ticket_number,
      });
    } catch (error) {
      toast({
        title: t("support.createFailed"),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const sendReply = async (event: FormEvent) => {
    event.preventDefault();
    if (!selected || !draft.trim() || busy) return;
    setBusy(true);
    const message = draft.trim();
    setDraft("");
    try {
      if (socket?.connected) {
        await new Promise<void>((resolve, reject) =>
          socket.emit(
            "support:message",
            {
              ticket_id: selected.id,
              message,
              visibility: internalNote ? "INTERNAL" : "PUBLIC",
            },
            (ack: { success: boolean; error?: { message?: string } }) =>
              ack.success
                ? resolve()
                : reject(
                    new Error(ack.error?.message ?? "Support chat failed."),
                  ),
          ),
        );
      } else {
        await publicApi(`${detailPath(selected.id)}/messages`, {
          method: "POST",
          body: { message, visibility: internalNote ? "INTERNAL" : "PUBLIC" },
        });
      }
      const result = await publicApi<Ticket>(detailPath(selected.id));
      setSelected(result.data);
      await loadTickets();
    } catch (error) {
      setDraft(message);
      toast({
        title: t("support.replyFailed"),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const updateTicket = async (
    changes: Partial<Pick<Ticket, "status" | "priority" | "assigned_role">>,
  ) => {
    if (!selected || !canManage) return;
    setBusy(true);
    try {
      const result = await publicApi<Ticket>(detailPath(selected.id), {
        method: "PATCH",
        body: changes,
      });
      setSelected({ ...result.data, messages: selected.messages ?? [] });
      await loadTickets();
    } catch (error) {
      toast({
        title: t("support.updateFailed"),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  if (adminMode && !isSupportRole)
    return (
      <div className="p-8 text-sm text-muted-foreground">
        {t("support.noAccess")}
      </div>
    );

  return (
    <div className="mx-auto max-w-7xl space-y-5 p-4 sm:p-7">
      <PageHead
        title={adminMode ? t("support.adminTitle") : t("support.title")}
        sub={adminMode ? t("support.adminSubtitle") : t("support.subtitle")}
      />
      <div className="grid min-h-[650px] gap-5 xl:grid-cols-[340px_minmax(0,1fr)]">
        <aside className="flex flex-col overflow-hidden rounded-2xl border bg-card">
          <div className="space-y-3 border-b p-4">
            <div className="flex items-center justify-between gap-2">
              <h2 className="font-semibold">{t("support.ticketList")}</h2>
              <span
                className={cn(
                  "rounded-full px-2.5 py-1 text-[11px]",
                  connected
                    ? "bg-emerald-100 text-emerald-800"
                    : "bg-amber-100 text-amber-800",
                )}
              >
                {t(connected ? "support.live" : "support.connecting")}
              </span>
            </div>
            {adminMode && (
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={t("support.search")}
              />
            )}
            <select
              className="h-10 w-full rounded-md border bg-background px-3 text-sm"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="">{t("support.allStatuses")}</option>
              {statusValues.map((value) => (
                <option key={value} value={value}>
                  {t(`support.status.${value}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 divide-y overflow-y-auto">
            {loading ? (
              <div className="flex justify-center p-8">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : (
              visibleTickets.map((ticket) => (
                <button
                  key={ticket.id}
                  type="button"
                  onClick={() => void openTicket(ticket)}
                  className={cn(
                    "w-full p-4 text-left transition hover:bg-muted/50",
                    selected?.id === ticket.id && "bg-primary/5",
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-xs font-semibold text-primary">
                      {ticket.ticket_number}
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      {t(`support.status.${ticket.status}`)}
                    </span>
                  </div>
                  <p className="mt-1 truncate text-sm font-medium">
                    {ticket.subject}
                  </p>
                  {adminMode && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {ticket.assigned_role} · {ticket.category}
                    </p>
                  )}
                </button>
              ))
            )}
            {!loading && visibleTickets.length === 0 && (
              <p className="p-8 text-center text-sm text-muted-foreground">
                {t("support.empty")}
              </p>
            )}
          </div>
          {!adminMode && (
            <div className="border-t p-3">
              <Button
                className="w-full"
                onClick={() =>
                  setSelected({
                    id: "",
                    ticket_number: "",
                    subject: "",
                    description: "",
                    category: "GENERAL",
                    priority: "NORMAL",
                    status: "OPEN",
                    assigned_role: "OPERATIONS_AGENT",
                    last_message_at: null,
                    messages: [],
                  })
                }
              >
                <Plus className="mr-2 h-4 w-4" />
                {t("support.newTicket")}
              </Button>
            </div>
          )}
        </aside>

        <main className="flex min-h-[650px] flex-col overflow-hidden rounded-2xl border bg-card">
          {selected?.id ? (
            <>
              <header className="flex flex-wrap items-start justify-between gap-3 border-b p-4 sm:p-5">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-primary">
                    {selected.ticket_number}
                  </p>
                  <h2 className="mt-1 text-lg font-bold">{selected.subject}</h2>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {selected.category} · {selected.assigned_role}
                  </p>
                </div>
                {adminMode && canManage ? (
                  <div className="flex flex-wrap gap-2">
                    <select
                      className="h-9 rounded-md border bg-background px-2 text-xs"
                      value={selected.status}
                      onChange={(event) =>
                        void updateTicket({
                          status: event.target.value as TicketStatus,
                        })
                      }
                    >
                      {statusValues.map((value) => (
                        <option key={value} value={value}>
                          {t(`support.status.${value}`)}
                        </option>
                      ))}
                    </select>
                    <select
                      className="h-9 rounded-md border bg-background px-2 text-xs"
                      value={selected.assigned_role}
                      onChange={(event) =>
                        void updateTicket({ assigned_role: event.target.value })
                      }
                    >
                      {queueRoles.map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                    <select
                      className="h-9 rounded-md border bg-background px-2 text-xs"
                      value={selected.priority}
                      onChange={(event) =>
                        void updateTicket({ priority: event.target.value })
                      }
                    >
                      {["LOW", "NORMAL", "HIGH", "URGENT"].map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <span className="rounded-full bg-muted px-3 py-1.5 text-xs">
                    {t(`support.status.${selected.status}`)}
                  </span>
                )}
              </header>
              <section
                className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-6"
                aria-live="polite"
              >
                {(selected.messages ?? []).map((message) => (
                  <article
                    key={message.id}
                    className={cn(
                      "max-w-[88%] rounded-2xl p-4 text-sm",
                      message.author_type === "MERCHANT"
                        ? "mr-auto bg-muted"
                        : "ml-auto bg-primary/10",
                      message.visibility === "INTERNAL" &&
                        "border border-amber-300 bg-amber-50 text-amber-950",
                    )}
                  >
                    <div className="mb-1 flex items-center gap-2 text-[11px] font-semibold text-muted-foreground">
                      <span>
                        {message.author_type === "ADMIN"
                          ? t("support.admin")
                          : message.author_type === "SYSTEM"
                            ? t("support.system")
                            : t("support.merchant")}
                      </span>
                      {message.visibility === "INTERNAL" && (
                        <span>{t("support.internalNote")}</span>
                      )}
                      <time>
                        {new Date(message.created_at).toLocaleString()}
                      </time>
                    </div>
                    <p className="whitespace-pre-wrap break-words leading-6">
                      {message.message}
                    </p>
                  </article>
                ))}
              </section>
              {selected.status !== "CLOSED" &&
                selected.status !== "RESOLVED" && (
                  <form
                    onSubmit={(event) => void sendReply(event)}
                    className="space-y-2 border-t p-3 sm:p-4"
                  >
                    {adminMode && (
                      <label className="flex w-fit items-center gap-2 text-xs text-muted-foreground">
                        <input
                          type="checkbox"
                          checked={internalNote}
                          onChange={(event) =>
                            setInternalNote(event.target.checked)
                          }
                        />
                        {t("support.internalNote")}
                      </label>
                    )}
                    <div className="flex items-end gap-2">
                      <Label htmlFor="support-reply" className="sr-only">
                        {t("support.replyPlaceholder")}
                      </Label>
                      <textarea
                        id="support-reply"
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                        maxLength={8000}
                        rows={3}
                        placeholder={t("support.replyPlaceholder")}
                        className="min-h-20 flex-1 resize-y rounded-xl border bg-background p-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                      />
                      <Button type="submit" disabled={busy || !draft.trim()}>
                        <Send className="mr-2 h-4 w-4" />
                        {t("support.send")}
                      </Button>
                    </div>
                  </form>
                )}
            </>
          ) : selected && !selected.id && !adminMode ? (
            <form
              onSubmit={(event) => void createTicket(event)}
              className="m-auto w-full max-w-xl space-y-5 p-6"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Headset className="h-6 w-6" />
              </div>
              <h2 className="text-xl font-bold">{t("support.newTicket")}</h2>
              <div className="space-y-2">
                <Label htmlFor="ticket-subject">{t("support.subject")}</Label>
                <Input
                  id="ticket-subject"
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  maxLength={180}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ticket-category">{t("support.category")}</Label>
                <select
                  id="ticket-category"
                  className="h-10 w-full rounded-md border bg-background px-3 text-sm"
                  value={category}
                  onChange={(event) => setCategory(event.target.value)}
                >
                  {[
                    "GENERAL",
                    "PAYMENT",
                    "PAYOUT",
                    "REFUND",
                    "BILLING",
                    "TECHNICAL",
                    "API",
                    "WEBHOOK",
                    "ACCOUNT",
                    "VERIFICATION",
                    "RISK",
                  ].map((value) => (
                    <option key={value} value={value}>
                      {t(`support.categories.${value}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="ticket-message">{t("support.message")}</Label>
                <textarea
                  id="ticket-message"
                  value={newMessage}
                  onChange={(event) => setNewMessage(event.target.value)}
                  maxLength={8000}
                  rows={6}
                  required
                  className="w-full rounded-xl border bg-background p-3 text-sm"
                />
              </div>
              <Button
                type="submit"
                disabled={busy || !subject.trim() || !newMessage.trim()}
              >
                {busy ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="mr-2 h-4 w-4" />
                )}
                {t("support.create")}
              </Button>
            </form>
          ) : (
            <div className="m-auto max-w-md p-8 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-violet-100 text-violet-700">
                <MessageSquare className="h-7 w-7" />
              </div>
              <h2 className="mt-4 text-lg font-bold">
                {t("support.selectTitle")}
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                {adminMode ? t("support.adminEmpty") : t("support.selectBody")}
              </p>
              <p className="mt-4 inline-flex items-center gap-2 text-xs text-muted-foreground">
                <ShieldCheck className="h-4 w-4" />
                {t("support.secureChat")}
              </p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
